import React from 'react';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Music, ExternalLink, Play, Heart } from 'lucide-react';

export const AlbumPromotion = () => {
  const streamingPlatforms = [
    {
      name: 'Apple Music',
      url: 'https://music.apple.com/us/search?term=nadja%20scott%20%26%20divine%20anointing',
      bgColor: 'bg-gray-900',
      textColor: 'text-white',
      hoverColor: 'hover:bg-gray-800'
    },
    {
      name: 'Spotify',
      url: 'https://open.spotify.com/artist/26fBJPfxi4NUAJbht7qYH6',
      bgColor: 'bg-green-600',
      textColor: 'text-white',
      hoverColor: 'hover:bg-green-700'
    },
    {
      name: 'YouTube',
      url: 'https://www.youtube.com/results?search_query=nadja+scott+divine+anointing+choir',
      bgColor: 'bg-red-600',
      textColor: 'text-white',
      hoverColor: 'hover:bg-red-700'
    },
    {
      name: 'Amazon Music',
      url: 'https://music.amazon.com/search/nadja+scott+divine+anointing+choir?filter=IsLibrary%7Cfalse&sc=none',
      bgColor: 'bg-orange-500',
      textColor: 'text-white',
      hoverColor: 'hover:bg-orange-600'
    },
    {
      name: 'Pandora',
      url: 'https://www.pandora.com/artist/nadja-scott-and-divine-anointing/forever-follow/ALcVn5cgJbdwhh9',
      bgColor: 'bg-blue-600',
      textColor: 'text-white',
      hoverColor: 'hover:bg-blue-700'
    }
  ];

  return (
    <section id="album" className="py-20 bg-gradient-to-br from-red-600 to-black">
      <div className="container mx-auto px-6">
        {/* Header */}
        <div className="text-center mb-16">
          <div className="flex items-center justify-center mb-6">
            <Music className="w-12 h-12 text-white mr-4" />
            <h2 className="text-4xl md:text-6xl font-bold text-white">
              NEW ALBUM OUT NOW!
            </h2>
          </div>
          <div className="inline-block bg-yellow-400 text-black px-6 py-2 rounded-full font-bold text-lg mb-6">
            🎵 AVAILABLE ON ALL DIGITAL PLATFORMS 🎵
          </div>
        </div>

        {/* Album Showcase */}
        <div className="max-w-6xl mx-auto">
          <Card className="bg-white/10 backdrop-blur-md border border-white/20 overflow-hidden">
            <CardContent className="p-0">
              <div className="grid lg:grid-cols-2 gap-0">
                {/* Album Cover */}
                <div className="relative aspect-square lg:aspect-auto">
                  <img 
                    src="https://customer-assets.emergentagent.com/job_nadja-divine-choir/artifacts/862fmld0_354556512_768117021769124_4539349280878477907_n.jpg"
                    alt="The Declaration Album Cover - Nadja Scott & Divine Anointing"
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent flex items-end">
                    <div className="p-6">
                      <div className="bg-red-600 text-white px-3 py-1 rounded-full text-sm font-bold mb-2">
                        DEBUT ALBUM
                      </div>
                    </div>
                  </div>
                </div>

                {/* Album Info */}
                <div className="p-8 lg:p-12 flex flex-col justify-center">
                  <h3 className="text-4xl md:text-5xl font-bold text-white mb-4">
                    "The Declaration"
                  </h3>
                  <p className="text-2xl text-white/90 mb-2">
                    Nadja Scott & Divine Anointing
                  </p>
                  <p className="text-lg text-white/70 mb-8">
                    Our debut album featuring powerful gospel anthems that declare God's goodness and grace. 
                    Experience the ministry that has touched hearts across the nation.
                  </p>

                  {/* Features */}
                  <div className="mb-8">
                    <h4 className="text-xl font-bold text-white mb-4">Album Highlights:</h4>
                    <ul className="space-y-2 text-white/90">
                      <li className="flex items-center">
                        <Heart className="w-4 h-4 text-red-400 mr-3" />
                        Powerful worship anthems
                      </li>
                      <li className="flex items-center">
                        <Heart className="w-4 h-4 text-red-400 mr-3" />
                        Live performance recordings
                      </li>
                      <li className="flex items-center">
                        <Heart className="w-4 h-4 text-red-400 mr-3" />
                        Original compositions
                      </li>
                      <li className="flex items-center">
                        <Heart className="w-4 h-4 text-red-400 mr-3" />
                        Featured collaborations
                      </li>
                    </ul>
                  </div>

                  {/* Streaming Platforms */}
                  <div>
                    <h4 className="text-xl font-bold text-white mb-4">Stream Now On:</h4>
                    <div className="grid grid-cols-2 gap-3">
                      {streamingPlatforms.map((platform, index) => (
                        <a
                          key={index}
                          href={platform.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className={`${platform.bgColor} ${platform.textColor} ${platform.hoverColor} px-4 py-3 rounded-lg text-sm font-medium transition-all flex items-center justify-center hover:scale-105`}
                        >
                          <ExternalLink className="w-4 h-4 mr-2" />
                          {platform.name}
                        </a>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Call to Action */}
        <div className="text-center mt-16">
          <p className="text-white text-xl mb-6">
            Support our ministry by streaming and sharing "The Declaration" today!
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button className="bg-white text-red-600 hover:bg-gray-100 px-8 py-3 rounded-full text-lg font-bold">
              <Play className="w-5 h-5 mr-2" />
              Listen Now
            </Button>
            <Button variant="outline" className="border-white text-white hover:bg-white/10 px-8 py-3 rounded-full text-lg">
              <Heart className="w-5 h-5 mr-2" />
              Share the Music
            </Button>
          </div>
        </div>
      </div>
    </section>
  );
};